/**
 * useOffline - Hook for offline detection and service worker management
 */
import { useState, useEffect, useCallback } from 'react';

export function useOffline() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [swStatus, setSwStatus] = useState(() => {
    // Initial status check
    if (!('serviceWorker' in navigator)) {
      return 'unsupported';
    }
    return 'checking';
  });
  const [cacheStatus, setCacheStatus] = useState(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
    };
    
    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Register service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const registerSW = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });


        if (registration.installing) {
          setSwStatus('installing');
          registration.installing.addEventListener('statechange', (e) => {
            if (e.target.state === 'activated') {
              setSwStatus('active');
            }
          });
        } else if (registration.waiting) {
          setSwStatus('waiting');
        } else if (registration.active) {
          setSwStatus('active');
        }

        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000); // Check every hour

      } catch (error) {
        setSwStatus('error');
      }
    };

    registerSW();

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      const { type, url } = event.data || {};
      
      if (type === 'CACHE_UPDATED') {
      }
    });
  }, []);

  // Get cache status from service worker
  const refreshCacheStatus = useCallback(async () => {
    if (!navigator.serviceWorker?.controller) {
      return null;
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        setCacheStatus(event.data);
        resolve(event.data);
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_CACHE_STATUS' },
        [messageChannel.port2]
      );

      // Timeout after 5 seconds
      setTimeout(() => resolve(null), 5000);
    });
  }, []);

  // Clear all caches
  const clearCache = useCallback(async () => {
    if (!navigator.serviceWorker?.controller) {
      return false;
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        setCacheStatus(null);
        resolve(event.data?.success || false);
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'CLEAR_CACHE' },
        [messageChannel.port2]
      );

      setTimeout(() => resolve(false), 5000);
    });
  }, []);

  // Pre-cache a specific state's data
  const cacheState = useCallback(async (state) => {
    if (!navigator.serviceWorker?.controller) {
      return { success: false, error: 'Service worker not active' };
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'CACHE_STATE', payload: { state } },
        [messageChannel.port2]
      );

      setTimeout(() => resolve({ success: false, error: 'Timeout' }), 30000);
    });
  }, []);

  return {
    isOffline,
    swStatus,
    cacheStatus,
    refreshCacheStatus,
    clearCache,
    cacheState
  };
}

export default useOffline;
